import React, { useState, useMemo, useEffect } from 'react'
import { TabButton } from '@tecuity/granite-ui/TabButton'
import { TabPanel } from '@tecuity/granite-ui/TabPanel'
import { TabProvider } from '@tecuity/granite-ui/TabProvider'
import { Body, Drawer } from '../../Shell/Shell'
import NotaryTabPanel from './NotaryTabPanel'
import NotaryComplaintsPanel, { ComplaintDetail } from './NotaryComplaintsPanel'
import NotesPanel from '../NotesPanel'
import { useLocation } from 'react-router'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { saveNewNote, editNote, deleteNote, getEntityNotes } from '../RecordDetailsActions'
import { Spinner } from '../../Loaders/Loaders'
import GenericDetails from '../GenericDetails'
import { setErrorToast } from '../../../actions'
import * as api from '../../../requests'
import RecordHistory from '../../RecordHistory/RecordHistory'
import { SET_RECORD_DETAILS } from '../RecordDetailsReducer'
import { useHistory } from 'react-router'

const NotaryRecordDetails = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const history = useHistory()
  const userRights = useSelector((state) => state.auth.appUserRights, shallowEqual)
  const { recordDetails = {} } = useSelector((state) => state.record, shallowEqual)
  const [activeTabId, setActiveTabId] = useState('tab-1')
  const [showDrawer, setShowDrawer] = useState(false)
  const [record, setRecord] = useState(null)
  const [recordDetailHistory, setHistory] = useState(null)
  const [recordDetailSettings, setDetailsSettings] = useState(null)
  const [activeRecordDisplaySettings, setActiveRecordDisplaySettings] = useState(null)

  const commissionNum = useMemo(() => {
    const parts = location.pathname.split('/')
    return parts[parts.length - 1]
  }, [location.pathname])

  useEffect(() => {
    if (!userRights.includes('NotaryView')) {
      dispatch(setErrorToast('Missing BusinessView User Right.'))
      history.goBack()
      return
    }

    api.GET_NOTARY_RECORD_DETAILS(
      commissionNum,
      (notary) => {
        setRecord(notary)
        dispatch({ type: SET_RECORD_DETAILS, record: notary })
        //850 is source type for notary. We may want to make this more dynamic at some point
        dispatch(getEntityNotes(850, notary.NotaryRecordId))
      },
      (err) => {
        console.error(err)
      }
    )

    api.GET_NOTARY_RECORD_DETAIL_SETTINGS(
      (recordDetailSettings) => {
        setDetailsSettings(recordDetailSettings)
      },
      (err) => {
        // setErrorToast('Failed to Retrieve Notary Display Settings')
        console.error(err)
      }
    )

    api.GET_NOTARY_RECORD_DETAIL_HISTORY(
      commissionNum,
      (recordDetailHistory) => {
        setHistory(recordDetailHistory)
      },
      (err) => {
        console.error(err)
      }
    )
  }, [])

  useEffect(() => {
    if (recordDetailSettings && record && recordDetailHistory) {
      setActiveRecordDisplaySettings(recordDetailSettings[0] ? recordDetailSettings[0] : null)
    }
  }, [recordDetailSettings, record, recordDetailHistory])

  const handleTabChange = (tabId) => {
    setActiveTabId(tabId)
    setShowDrawer(false)
    if (tabId) {
      window.setTimeout(() => {
        window.scroll()
      }, 300)
    }
  }

  const saveNewNotaryNote = (note) => {
    //850 is source type for notary. We may want to make this more dynamic at some point
    dispatch(saveNewNote(note, 850, recordDetails.NotaryRecordId))
  }

  const editNotaryNote = (note) => {
    //850 is source type for notary. We may want to make this more dynamic at some point
    dispatch(editNote(note, 850, recordDetails.NotaryRecordId))
  }

  const deleteNotaryNote = (noteId) => {
    //850 is source type for notary. We may want to make this more dynamic at some point
    dispatch(deleteNote(noteId, 850, recordDetails.NotaryRecordId))
  }

  if (!activeRecordDisplaySettings) {
    return (
      <div style={{ height: '100%', width: '100%', textAlign: 'center', padding: '4rem' }}>
        <Spinner className="page-loader" themeColor="dark-neutral" />
      </div>
    )
  }

  return (
    <Body className="no-padding">
      <TabProvider activeTabId={activeTabId} onChange={(id) => handleTabChange(id)}>
        <div className="row header-row">
          <h1>Notary Record Details</h1>
          <div className="tablist" role="tablist">
            {activeRecordDisplaySettings &&
              (activeRecordDisplaySettings.Tabs || []).map(
                (tab, i) =>
                  tab.Title !== 'General' &&
                  tab.Enabled && (
                    <TabButton
                      key={`tab-${i}`}
                      id={`tab-${i}`}
                      forPanelId={`panel-${i}`}
                      label={tab.Title}
                      customCss={
                        tab.Title === 'Notes' && record['SeeNotesFlag'] === true
                          ? { backgroundColor: 'red !important', color: '#fff !important' }
                          : {}
                      }
                    />
                  )
              )}
          </div>
        </div>

        <GenericDetails record={record} config={activeRecordDisplaySettings} />

        <div>
          {activeRecordDisplaySettings &&
            (activeRecordDisplaySettings.Tabs || []).map(
              (tab, i) =>
                tab.Title !== 'General' &&
                tab.Enabled && (
                  <TabPanel
                    key={`panel-${i}`}
                    id={`panel-${i}`}
                    tabId={`tab-${i}`}
                    label={tab.Title}
                  >
                    {tab.Title === 'Notes' ? (
                      <NotesPanel
                        saveNote={saveNewNotaryNote}
                        editNote={editNotaryNote}
                        deleteNote={deleteNotaryNote}
                      />
                    ) : tab.Title === 'History' ? (
                      <React.Fragment>
                        <h2>History</h2>
                        <RecordHistory
                          history={{
                            amendments: recordDetailHistory.AMENDMENT_LIST,
                            history: recordDetailHistory.HISTORY_LIST,
                            template: activeRecordDisplaySettings.HistoryConfig.filter(
                              (t) => t.enabled
                            ),
                          }}
                          requiredVoidRight="NotaryVoid"
                        />
                      </React.Fragment>
                    ) : tab.Title === 'Complaints' ? (
                      <NotaryComplaintsPanel setShowDrawer={setShowDrawer} />
                    ) : tab.Title === 'Special Services' ? (
                      <NotaryTabPanel
                        fieldSettings={
                          (activeRecordDisplaySettings.FieldSettings.ServiceProviderTableSettings &&
                            activeRecordDisplaySettings.FieldSettings.ServiceProviderTableSettings
                              .Fields) ||
                          {}
                        }
                        data={record && record.NotarySoftwares ? record.NotarySoftwares : []}
                        title={tab.Title}
                      />
                    ) : (
                      'No information for tab'
                    )}
                  </TabPanel>
                )
            )}
        </div>
        <Drawer
          showCloseButton
          show={showDrawer}
          title={'Complaint Details'}
          onSidebarDismiss={() => {
            setShowDrawer(false)
          }}
        >
          <ComplaintDetail />
        </Drawer>
      </TabProvider>
    </Body>
  )
}

export default NotaryRecordDetails
